import { useEffect, useState, useMemo } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer,
} from "recharts";
import { TrendingUp, TrendingDown, Trophy } from "lucide-react";
import api from "@/lib/api";

/**
 * RadarExpandModal (iter66) — clicked from the SkillSnapshotRadar in the
 * Advanced view. Enlarges the radar and splits per-axis levels into
 * Strengths (at or above the next DUPR level target) and Focus Areas
 * (below the next-level target).
 *
 * Next DUPR level = current mean rounded up to the nearest 0.5.
 */
export default function RadarExpandModal({ studentId, open, onOpenChange, color = "#CCFF00" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !studentId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/players/${studentId}/skill-snapshot`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, studentId]);

  const chartRows = useMemo(() => {
    if (!data) return [];
    const cur = data.current?.axes || {};
    return (data.axes_order || []).map((axis) => ({
      skill: axis,
      current: cur[axis] == null ? null : Number(cur[axis]),
    }));
  }, [data]);

  const { nextTarget, strengths, focus, currentMean } = useMemo(() => {
    const values = chartRows.filter((r) => r.current != null).map((r) => r.current);
    if (values.length === 0) return { nextTarget: null, strengths: [], focus: [], currentMean: null };
    const mean = values.reduce((s, v) => s + v, 0) / values.length;
    // Next DUPR level = ceil(mean * 2) / 2 (round UP to next 0.5)
    let target = Math.ceil(mean * 2) / 2;
    if (target <= mean) target += 0.5; // ensure strictly above
    target = Math.min(5.5, target);
    const strengthsList = chartRows.filter((r) => r.current != null && r.current >= target)
      .sort((a, b) => b.current - a.current);
    const focusList = chartRows.filter((r) => r.current != null && r.current < target)
      .map((r) => ({ ...r, gap: +(target - r.current).toFixed(2) }))
      .sort((a, b) => b.gap - a.gap);
    return { nextTarget: target, strengths: strengthsList, focus: focusList, currentMean: +mean.toFixed(2) };
  }, [chartRows]);

  const ringMin = data?.ring_config?.min ?? 2.5;
  const ringMax = data?.ring_config?.max ?? 5.5;
  const ringStep = data?.ring_config?.step ?? 0.25;
  const gridCount = Math.round((ringMax - ringMin) / ringStep);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl bg-[#0A0A0A] border-[#262626] text-[#E5E5E5]" data-testid="radar-expand-modal">
        <DialogHeader>
          <DialogTitle className="h-display text-2xl tracking-tight">Skill snapshot · full view</DialogTitle>
          <DialogDescription className="text-sm text-[#A3A3A3]">
            {currentMean != null ? (
              <>Your current average is <strong style={{ color }}>{currentMean}</strong>. Next DUPR target: <strong style={{ color }}>{nextTarget}</strong>. Strengths are axes at or above target; focus areas are below.</>
            ) : (
              "Loading your snapshot…"
            )}
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="text-sm text-[#A3A3A3] py-8 text-center">Loading…</div>}

        {!loading && chartRows.every((r) => r.current == null) && (
          <div className="text-sm text-[#A3A3A3] py-8 text-center">
            No ratings on record yet. Once your coach ticks some competencies this chart will fill up.
          </div>
        )}

        {!loading && chartRows.some((r) => r.current != null) && (
          <div className="grid md:grid-cols-2 gap-6 mt-2">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={chartRows} outerRadius="80%">
                  <PolarGrid stroke="#262626" gridType="polygon" radialLines gridCount={gridCount} />
                  <PolarAngleAxis dataKey="skill" tick={{ fill: "#A3A3A3", fontSize: 11 }} />
                  <PolarRadiusAxis domain={[ringMin, ringMax]} tick={false} axisLine={false} stroke="transparent" />
                  <Radar name="Today" dataKey="current" stroke={color} fill={color} fillOpacity={0.28} isAnimationActive connectNulls={false} />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            <div className="space-y-4">
              <div data-testid="radar-strengths">
                <div className="label-mono mb-2 inline-flex items-center gap-1.5" style={{ color: "#CCFF00" }}>
                  <Trophy className="w-3.5 h-3.5" /> STRENGTHS · AT OR ABOVE {nextTarget}
                </div>
                {strengths.length ? (
                  <div className="space-y-1.5">
                    {strengths.map((s) => (
                      <div key={s.skill} className="flex items-center justify-between text-sm border-l-2 pl-3" style={{ borderColor: "#CCFF00" }}>
                        <span className="font-semibold">{s.skill}</span>
                        <span className="font-mono" style={{ color: "#CCFF00" }}>{s.current}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-[#A3A3A3]">Nothing at your next level yet — everything is a focus area.</div>
                )}
              </div>

              <div data-testid="radar-focus">
                <div className="label-mono mb-2 inline-flex items-center gap-1.5" style={{ color: "#FF4500" }}>
                  <TrendingDown className="w-3.5 h-3.5" /> FOCUS · BELOW {nextTarget}
                </div>
                {focus.length ? (
                  <div className="space-y-1.5">
                    {focus.map((f) => (
                      <div key={f.skill} className="flex items-center justify-between text-sm border-l-2 pl-3" style={{ borderColor: "#FF4500" }}>
                        <span className="font-semibold">{f.skill}</span>
                        <span className="font-mono text-[#A3A3A3]">
                          {f.current} <TrendingUp className="w-3 h-3 inline mx-1" style={{ color: "#FF4500" }} /> {nextTarget}
                          <span className="ml-2 text-[#FF4500]">+{f.gap}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-[#A3A3A3]">Everything is at or above the next-level target. Ask your coach to raise the bar.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
